import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { DashboardShell } from '../../components/DashboardShell.js';
import { useCurrentOrg, useOrgEvents, type OrgEvent } from '../../lib/orgs.js';
import { OnboardingPage } from './OnboardingPage.js';

export function EventListPage() {
  const { t } = useTranslation('dashboard');
  const { lang } = useParams();
  const org = useCurrentOrg();
  const events = useOrgEvents(org?.id ?? null);

  if (!org) return <OnboardingPage />;

  return (
    <DashboardShell>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
        <h1 className="text-3xl font-bold">{t('events.list_title')}</h1>
        <Link to={`/${lang}/dashboard/events/new`} className="btn-primary">
          {t('events.create_button')}
        </Link>
      </div>

      {events.isLoading ? (
        <p className="text-neutral-500 text-sm">{t('event_list.loading')}</p>
      ) : events.data && events.data.events.length > 0 ? (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {events.data.events.map((e) => (
            <EventRow key={e.id} event={e} lang={lang ?? 'es'} t={t} />
          ))}
        </ul>
      ) : (
        <div className="card text-center text-neutral-400">
          <h2 className="text-xl font-semibold mb-2">{t('events.empty_title')}</h2>
          <p>{t('events.empty_body')}</p>
          <Link to={`/${lang}/dashboard/events/new`} className="btn-primary mt-6 inline-flex">
            {t('events.create_button')}
          </Link>
        </div>
      )}
    </DashboardShell>
  );
}

function EventRow({ event, lang, t }: { event: OrgEvent; lang: string; t: (k: string) => string }) {
  const tr = event.translations[lang] ?? event.translations[event.defaultLocale];
  const title = tr?.title ?? event.slug;
  const fmtDate = new Intl.DateTimeFormat(lang, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const status = t(`events.status.${event.status}`);
  const color =
    event.status === 'live'
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
      : event.status === 'draft'
        ? 'bg-neutral-700/40 text-neutral-300 border-neutral-700'
        : 'bg-neutral-900 text-neutral-500 border-neutral-800';
  return (
    <li className="card flex flex-col gap-3 hover:border-neutral-700 transition">
      <Link to={`/${lang}/dashboard/events/${event.id}`} className="flex flex-col gap-2 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-lg leading-tight break-words min-w-0">{title}</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${color}`}>
            {status}
          </span>
        </div>
        <p className="text-sm text-neutral-400">
          {fmtDate.format(new Date(event.startsAt))} · {event.timezone}
        </p>
        <p className="text-xs text-neutral-500 truncate">
          /{event.slug} · {event.currency}
        </p>
      </Link>
      <div className="flex gap-2 pt-1 border-t border-neutral-900">
        <Link
          to={`/${lang}/dashboard/events/${event.id}`}
          className="text-xs sm:text-sm px-3 py-2 rounded-lg border border-neutral-800 hover:border-neutral-600 transition flex-1 text-center"
        >
          {t('events.edit_event')}
        </Link>
        <Link
          to={`/${lang}/dashboard/events/${event.id}/sales`}
          className="text-xs sm:text-sm px-3 py-2 rounded-lg border border-neutral-800 hover:border-neutral-600 transition flex-1 text-center"
        >
          {t('events.view_sales')}
        </Link>
      </div>
    </li>
  );
}
